/**
 * frontend/src/components/BottomNav.jsx
 * SoundDAO — Fixed bottom tab bar for mobile (≤1023px) / sidebar for desktop (≥1024px)
 *
 * Sprint 6A — Rex-first four-tab architecture (Player persona):
 *   🎧 Rex        → /player/rex
 *   📊 Dashboard  → /player/dashboard
 *   🏆 Rankings   → /player/rankings
 *   🔍 Discover   → /player/discover   (also lights up on /dig)
 *   🎵 Playlists  → /playlists         (Sprint 6A)
 *
 * Sprint 6B follow-up — Agent tab added as first item for every persona:
 *   Creator   → 🎤 Ivy   → /creator/ivy
 *   Collector → 📊 Atlas → /collector/atlas
 * Remaining creator/collector tabs:
 *   🏠 Dashboard  → /dashboard/:persona
 *   🔍 Discover   → /creators
 *   🏆 Rankings   → /leaderboard
 *   👤 Profile    → /creator/:id (creators) | /settings (collectors)
 *
 * Rendered by App.js when session + persona exist and path is not nav-free.
 */

import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import './BottomNav.css';

const NAV_FREE_PREFIXES = [
  '/auth',
  '/onboarding',
  '/reward',
  '/invite/',
  '/creator-onboarding',
  '/artist-pick',
];

export function shouldShowBottomNav(pathname) {
  return !NAV_FREE_PREFIXES.some(p => pathname === p || pathname.startsWith(p));
}

function getPlayerTabs() {
  return [
    { to: '/player/rex',       icon: '🎧', label: 'Rex',       matchPaths: ['/player/rex'] },
    { to: '/player/dashboard', icon: '📊', label: 'Dashboard', matchPaths: ['/player/dashboard', '/dashboard/player'] },
    { to: '/player/rankings',  icon: '🏆', label: 'Rankings',  matchPaths: ['/player/rankings', '/leaderboard'] },
    { to: '/player/discover',  icon: '🔍', label: 'Discover',  matchPaths: ['/player/discover', '/creators', '/dig', '/dig/'] },
    // Sprint 6A — built + linked playlists (Jeremy: nav tab as well as the dashboard link)
    { to: '/playlists',        icon: '🎵', label: 'Playlists', matchPaths: ['/playlists', '/playlists/'] },
  ];
}

function getLegacyTabs(user, hasCreatorProfile) {
  const dashboardPath = `/dashboard/${user?.persona || 'collector'}`;
  const isCreator     = user?.persona === 'creator';
  const isCollector   = user?.persona === 'collector';
  // Punch-list item 3. Two defects here:
  //   1. `user.id` was unguarded while the matchPaths below used `user?.id` —
  //      a null user would have thrown on this line.
  //   2. A creator with no completed `creators` row has no public profile, so
  //      /creator/:id 404s. Send them to finish onboarding instead.
  //      hasCreatorProfile is passed down from App.js; when it is undefined we
  //      have not checked yet, so keep the old behaviour rather than bouncing
  //      a real creator to onboarding on a slow load.
  const profilePath = isCreator
    ? (hasCreatorProfile === false ? '/creator-onboarding' : `/creator/${user?.id || ''}`)
    : '/settings';

  // Sprint 6B follow-up — agent tab prepended as the first item per persona.
  // Ivy for creators, Atlas for collectors.  Player has its own tab set
  // (getPlayerTabs) which already leads with Rex.
  const agentTab = isCreator
    ? { to: '/creator/ivy',     icon: '🎤', label: 'Ivy',   matchPaths: ['/creator/ivy'] }
    : isCollector
      ? { to: '/collector/atlas', icon: '📊', label: 'Atlas', matchPaths: ['/collector/atlas'] }
      : null;

  // Discover / Profile matchPaths are restricted so they do NOT light up
  // when the user is on the agent tab route.  Profile matches exactly the
  // user's own creator URL (/creator/:own_id); Discover only matches the
  // Discover grid path (/creators), not the profile detail prefix.
  const tabs = [
    ...(agentTab ? [agentTab] : []),
    { to: dashboardPath, icon: '🏠', label: 'Dashboard', matchPaths: ['/dashboard/'] },
    { to: '/creators',   icon: '🔍', label: 'Discover',  matchPaths: ['/creators', '/dig', '/dig/'] },
    // Sprint 6A — Dig + playlists for every persona, not just players.
    { to: '/playlists',  icon: '🎵', label: 'Playlists', matchPaths: ['/playlists', '/playlists/'] },
    { to: '/leaderboard',icon: '🏆', label: 'Rankings',  matchPaths: ['/leaderboard'] },
    {
      to: profilePath,
      icon: '👤',
      label: 'Profile',
      matchPaths: isCreator
        ? [`/creator/${user?.id || ''}`]   // exact own-profile path
        : ['/settings'],
    },
  ];

  return tabs;
}

export default function BottomNav({ user, hasCreatorProfile }) {
  const location = useLocation();

  if (!shouldShowBottomNav(location.pathname)) return null;

  const isPlayer = user?.persona === 'player';
  const tabs     = isPlayer ? getPlayerTabs() : getLegacyTabs(user, hasCreatorProfile);

  return (
    <nav className="bottom-nav" aria-label="Main navigation">
      {tabs.map(tab => {
        const isActive = tab.matchPaths.some(p =>
          p.endsWith('/') ? location.pathname.startsWith(p) : location.pathname === p
        );
        return (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={`bottom-nav-tab ${isActive ? 'bottom-nav-tab--active' : ''}`}
            aria-label={tab.label}
          >
            <span className="bottom-nav-icon">{tab.icon}</span>
            <span className="bottom-nav-label">{tab.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}
